import React from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";

// ContractStatus//

type ContractToggleKey = "show" | "track" | "email";
type ContractDrawerBoolField = ContractToggleKey | "isDefault";

export type ContractStatusDrawerMode = "add" | "edit";

export interface ContractStatusDrawerForm {
  id: string;
  status: string;
  statusName: string;
  color?: string;
  show: boolean;
  track: boolean;
  email: boolean;
  isDefault: boolean;
}

type ContractStatusDrawerProps = {
  open: boolean;
  mode: ContractStatusDrawerMode;
  form: ContractStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof ContractStatusDrawerForm>(
    field: K,
    value: ContractStatusDrawerForm[K]
  ) => void;

  onToggle: (field: ContractDrawerBoolField) => void;
};

export const ContractStatusDrawer: React.FC<ContractStatusDrawerProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  onToggle,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Contract Status" : "Edit Contract Status"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* ID + Status row */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="csId">
                  Contract ID
                </label>
                <input
                  id="csId"
                  className="drawer-input"
                  type="number"
                  min={1}
                  placeholder="ID"
                  value={form.id}
                  onChange={(e) => updateField("id", e.target.value)}
                />
              </div>

              <div className="drawer-field">
                <label className="drawer-label" htmlFor="csStatus">
                  Contract Status
                </label>
                <input
                  id="csStatus"
                  className="drawer-input"
                  type="text"
                  placeholder="Status"
                  value={form.status}
                  onChange={(e) => updateField("status", e.target.value)}
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="drawer-toggle-list">
              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Account Summary</span>
                <div
                  className={`toggle-switch ${form.show ? "is-on" : ""}`}
                  onClick={() => onToggle("show")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Track Count</span>
                <div
                  className={`toggle-switch ${form.track ? "is-on" : ""}`}
                  onClick={() => onToggle("track")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Email</span>
                <div
                  className={`toggle-switch ${form.email ? "is-on" : ""}`}
                  onClick={() => onToggle("email")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>
            </div>

            {/* Default + buttons */}
            <div className="drawer-bottom-row">
              <div className="drawer-bottom-group">
                <div className="drawer-toggle-item">
                  <span className="drawer-toggle-label">Default</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form.isDefault ? "is-on" : ""
                    }`}
                    onClick={() => onToggle("isDefault")}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>

                <div className="drawer-actions drawer-actions-under">
                  <button className="btn-close" type="button" onClick={onClose}>
                    Close
                  </button>

                  <button className="btn-reset" type="button" onClick={onReset}>
                    Reset
                  </button>

                  <button className="btn-save" type="button" onClick={onSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
            {/* end bottom row */}
          </div>
        </div>
      </aside>
    </>
  );
};


//ProjectStatus//
export type ProjectStatusDrawerMode = "add" | "edit";

export interface ProjectStatusDrawerForm {
  id: string;
  status: string;
}

type ProjectStatusDrawerProps = {
  open: boolean;
  mode: ProjectStatusDrawerMode;
  form: ProjectStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  onFieldChange: <K extends keyof ProjectStatusDrawerForm>(
    field: K,
    value: ProjectStatusDrawerForm[K]
  ) => void;
};

export const ProjectStatusDrawer: React.FC<ProjectStatusDrawerProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  onFieldChange,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Project Status" : "Edit Project Status"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="psId">
                  Project ID
                </label>
                <input
                  id="psId"
                  className="drawer-input"
                  type="number"
                  min={1}
                  placeholder="ID"
                  value={form.id}
                  onChange={(e) => onFieldChange("id", e.target.value)}
                />
              </div>

              <div className="drawer-field">
                <label className="drawer-label" htmlFor="psStatus">
                  Project Status
                </label>
                <input
                  id="psStatus"
                  className="drawer-input"
                  type="text"
                  placeholder="Project Status"
                  value={form.status}
                  onChange={(e) => onFieldChange("status", e.target.value)}
                />
              </div>
            </div>

            {/* Buttons aligned left */}
            <div className="drawer-bottom-row">
              <div className="drawer-actions drawer-actions-left">
                <button className="btn-close" type="button" onClick={onClose}>
                  Close
                </button>

                <button className="btn-reset" type="button" onClick={onReset}>
                  Reset
                </button>

                <button className="btn-save" type="button" onClick={onSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};


/*AccountStatusDrawer*/

type AccountToggleKey =
  | "rec"
  | "publ"
  | "mech"
  | "approval"
  | "control"
  | "email"
  | "auto";

type AccountDrawerBoolField = AccountToggleKey | "isDefault";
type AccountDrawerToggleField = Exclude<AccountDrawerBoolField, "isDefault">;

export interface AccountStatusDrawerForm {
  id: string;
  shortCode: string;
  name: string;
  rec: boolean;
  publ: boolean;
  mech: boolean;
  approval: boolean;
  control: boolean;
  email: boolean;
  auto: boolean;
  isDefault: boolean;
  color: string;
}

export type AccountStatusDrawerMode = "add" | "edit";

export const accountStatusDrawerToggleConfig: {
  key: AccountDrawerToggleField;
  label: string;
}[] = [
  { key: "rec", label: "Rec MYN" },
  { key: "publ", label: "Publ YN" },
  { key: "mech", label: "Mech YN" },
  { key: "approval", label: "Approval YN" },
  { key: "control", label: "Control Rpt YN" },
  { key: "email", label: "Email YN" },
  { key: "auto", label: "Auto LTD" },
];

type AccountStatusProps = {
  open: boolean;
  mode: AccountStatusDrawerMode;
  form: AccountStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof AccountStatusDrawerForm>(
    field: K,
    value: AccountStatusDrawerForm[K]
  ) => void;

  toggleBool: (field: AccountDrawerBoolField) => void;
};

export const AccountStatusDrawer: React.FC<AccountStatusProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  toggleBool,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer – same markup/styles you already use */}
      <aside className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title">
              {mode === "add" ? "Add Account Status" : "Edit Account Status"}
            </div>
            <button className="drawer-close" onClick={onClose} type="button">
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* Use ID */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asUseId">
                  Use ID
                </label>
                <input
                  id="asUseId"
                  className="drawer-input"
                  type="number"
                  placeholder="Use ID e.g. 70"
                  value={form.id}
                  onChange={(e) => updateField("id", e.target.value)}
                />
              </div>
            </div>

            {/* Short Code + Name row */}
            <div className="drawer-form-row">
              {/* Name on the LEFT */}
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asName">
                  Name
                </label>
                <input
                  id="asName"
                  className="drawer-input"
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              {/* Short Code on the RIGHT */}
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asShortCode">
                  Short Code
                </label>
                <input
                  id="asShortCode"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.shortCode}
                  onChange={(e) => updateField("shortCode", e.target.value)}
                />
              </div>
            </div>

            {/* Slider toggles */}
            <div className="drawer-toggle-row">
              {accountStatusDrawerToggleConfig.map(({ key, label }) => (
                <div className="drawer-toggle-item" key={key}>
                  <span className="drawer-toggle-label">{label}</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form[key as keyof AccountStatusDrawerForm] ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool(key)}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>
              ))}
            </div>

            {/* Default row + Close / Reset / Save buttons */}
            <div className="drawer-bottom-row">
              <div className="drawer-bottom-group">
                {/* Default toggle */}
                <div className="drawer-toggle-item">
                  <span className="drawer-toggle-label">Default</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form.isDefault ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool("isDefault")}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>

                {/* Buttons under Default slider */}
                <div className="drawer-actions drawer-actions-under">
                  <button className="btn-close" type="button" onClick={onClose}>
                    Close
                  </button>

                  <button className="btn-reset" type="button" onClick={onReset}>
                    Reset
                  </button>

                  <button className="btn-save" type="button" onClick={onSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};



/** AccountStatusExt drawer types */
type AccountExtToggleKey =
  | "rec"
  | "publ"
  | "mech"
  | "approval"
  | "control"
  | "email"
  | "auto";

type AccountExtDrawerBoolField = AccountExtToggleKey | "isDefault";

export type AccountStatusExtDrawerMode = "add" | "edit";

export interface AccountStatusExtDrawerForm {
  useId: string;
  parent: string;
  name: string;
  shortCode: string;
  rec: boolean;
  publ: boolean;
  mech: boolean;
  approval: boolean;
  control: boolean;
  email: boolean;
  auto: boolean;
  isDefault: boolean;
  color: string;
}

type AccountExtProps = {
  open: boolean;
  mode: AccountStatusExtDrawerMode;
  form: AccountStatusExtDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof AccountStatusExtDrawerForm>(
    field: K,
    value: AccountStatusExtDrawerForm[K]
  ) => void;

  toggleBool: (field: AccountExtDrawerBoolField) => void;
};

export const AccountStatusExtDrawer: React.FC<AccountExtProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  toggleBool,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Account Status Ext" : "Edit Account Status Ext"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asUseId">
                  Use ID
                </label>
                <input
                  id="asUseId"
                  className="drawer-input"
                  type="number"
                  placeholder="Use ID"
                  value={form.useId}
                  onChange={(e) => updateField("useId", e.target.value)}
                />
              </div>
            </div>

            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asName">
                  Name
                </label>
                <input
                  id="asName"
                  className="drawer-input"
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="drawer-field">
                <label className="drawer-label" htmlFor="asShort">
                  Short Code
                </label>
                <input
                  id="asShort"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.shortCode}
                  onChange={(e) => updateField("shortCode", e.target.value)}
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="drawer-toggle-row">
              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Rec MYN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.rec ? "is-on" : ""}`}
                  onClick={() => toggleBool("rec")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Publ YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.publ ? "is-on" : ""}`}
                  onClick={() => toggleBool("publ")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Mech YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.mech ? "is-on" : ""}`}
                  onClick={() => toggleBool("mech")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Approval YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.approval ? "is-on" : ""}`}
                  onClick={() => toggleBool("approval")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Control Rpt YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.control ? "is-on" : ""}`}
                  onClick={() => toggleBool("control")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Email YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.email ? "is-on" : ""}`}
                  onClick={() => toggleBool("email")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Auto LTD</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${form.auto ? "is-on" : ""}`}
                  onClick={() => toggleBool("auto")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>
            </div>

            {/* Default row + Close / Reset / Save buttons */}
            <div className="drawer-bottom-row">
              <div className="drawer-bottom-group">
                <div className="drawer-toggle-item">
                  <span className="drawer-toggle-label">Default</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form.isDefault ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool("isDefault")}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>

                <div className="drawer-actions drawer-actions-under">
                  <button className="btn-close" type="button" onClick={onClose}>
                    Close
                  </button>

                  <button className="btn-reset" type="button" onClick={onReset}>
                    Reset
                  </button>

                  <button className="btn-save" type="button" onClick={onSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
            {/* end bottom row */}
          </div>
        </div>
      </aside>
    </>
  );
};


/*StatementStatus*/
type StatementToggleKey =
  | "rec"
  | "publ"
  | "mech"
  | "approval"
  | "control"
  | "email"
  | "auto";

type StatementDrawerBoolField = StatementToggleKey | "isDefault";

export type StatementStatusDrawerMode = "add" | "edit";

export interface StatementStatusDrawerForm {
  id: string;
  shortCode: string;
  name: string;
  rec: boolean;
  publ: boolean;
  mech: boolean;
  approval: boolean;
  control: boolean;
  email: boolean;
  auto: boolean;
  isDefault: boolean;
  color: string;
}

type StatementProps = {
  open: boolean;
  mode: StatementStatusDrawerMode;
  form: StatementStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof StatementStatusDrawerForm>(
    field: K,
    value: StatementStatusDrawerForm[K]
  ) => void;

  toggleBool: (field: StatementDrawerBoolField) => void;
};

export const StatementStatusDrawer: React.FC<StatementProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  toggleBool,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Statement Status" : "Edit Statement Status"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* Use ID */}
            <div
              className="drawer-form-row"
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-end",
                marginBottom: 16,
              }}
            >
              <div className="drawer-field" style={{ flex: 1 }}>
                <label className="drawer-label" htmlFor="ssUseId">
                  Statement Status UseID
                </label>
                <input
                  id="ssUseId"
                  className="drawer-input"
                  type="number"
                  placeholder="Use ID e.g. 25"
                  value={form.id}
                  onChange={(e) => updateField("id", e.target.value)}
                />
              </div>
            </div>

            {/* Name + Short code */}
            <div
              className="drawer-form-row"
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-end",
                marginBottom: 16,
              }}
            >
              <div className="drawer-field" style={{ flex: 1 }}>
                <label className="drawer-label" htmlFor="ssName">
                  Statement Status Name
                </label>
                <input
                  id="ssName"
                  className="drawer-input"
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="drawer-field" style={{ flex: 1 }}>
                <label className="drawer-label" htmlFor="ssShortCode">
                  Statement Status Short Code
                </label>
                <input
                  id="ssShortCode"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.shortCode}
                  onChange={(e) => updateField("shortCode", e.target.value)}
                />
              </div>
            </div>

            {/* Toggles grid */}
            <div
              className="drawer-toggle-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                columnGap: 24,
                rowGap: 10,
                marginTop: 8,
              }}
            >
              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Rec MYN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.rec ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("rec")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Publ YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.publ ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("publ")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Mech YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.mech ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("mech")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Approval YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.approval ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("approval")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Control Rpt YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.control ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("control")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Email YN</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.email ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("email")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Auto LTD</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.auto ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("auto")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>
            </div>

            {/* Default + buttons under it */}
            <div
              className="drawer-bottom-row"
              style={{
                marginTop: 18,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Default</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.isDefault ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("isDefault")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div
                className="drawer-actions drawer-actions-left"
                style={{ display: "flex", gap: 10 }}
              >
                <button className="btn-close" type="button" onClick={onClose}>
                  Close
                </button>

                <button className="btn-reset" type="button" onClick={onReset}>
                  Reset
                </button>

                <button className="btn-save" type="button" onClick={onSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};


// PayeeStatusDrawer//

type ToggleKey =
  | "rec"
  | "publ"
  | "mech"
  | "approval"
  | "control"
  | "email"
  | "auto";

type DrawerBoolField = ToggleKey | "isDefault";

export type PayeeStatusDrawerMode = "add" | "edit";

export interface PayeeStatusDrawerForm {
  id: string;
  code: string;
  name: string;
  rec: boolean;
  publ: boolean;
  mech: boolean;
  approval: boolean;
  control: boolean;
  email: boolean;
  auto: boolean;
  isDefault: boolean;
  color?: string;
}

type PayeeProps = {
  open: boolean;
  mode: PayeeStatusDrawerMode;
  form: PayeeStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof PayeeStatusDrawerForm>(
    field: K,
    value: PayeeStatusDrawerForm[K]
  ) => void;

  toggleBool: (field: DrawerBoolField) => void;
};

export const PayeeStatusDrawer: React.FC<PayeeProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  toggleBool,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Payee Status" : "Edit Payee Status"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* Row 1: ID */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="psUseId">
                  Payee Status ID
                </label>
                <input
                  id="psUseId"
                  className="drawer-input"
                  type="number"
                  min={0}
                  step={10}
                  placeholder="Payee Status UseID"
                  value={form.id}
                  onChange={(e) => updateField("id", e.target.value)}
                />
              </div>
            </div>

            {/* Row 2: Name + Short Code */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="dsName">
                  Payee Status Name
                </label>
                <input
                  id="dsName"
                  className="drawer-input"
                  type="text"
                  placeholder="Status Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="drawer-field">
                <label className="drawer-label" htmlFor="dsShortCode">
                  Payee Status Short Code
                </label>
                <input
                  id="dsShortCode"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.code}
                  onChange={(e) => updateField("code", e.target.value)}
                />
              </div>
            </div>

            {/* Toggles */}
            <div
              className="drawer-toggle-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                columnGap: 32,
                rowGap: 10,
                marginTop: 8,
              }}
            >
              {(
                [
                  ["rec", "Rec MYN"],
                  ["publ", "Publ YN"],
                  ["mech", "Mech YN"],
                  ["approval", "Approval YN"],
                  ["control", "Control Rpt YN"],
                  ["email", "Email YN"],
                  ["auto", "Auto LTD"],
                ] as const
              ).map(([k, label]) => (
                <div className="drawer-toggle-item" key={k}>
                  <span className="drawer-toggle-label">{label}</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form[k] ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool(k)}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>
              ))}
            </div>

            {/* Default + buttons under it */}
            <div
              className="drawer-bottom-row"
              style={{
                marginTop: 18,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div className="drawer-toggle-item">
                <span className="drawer-toggle-label">Default</span>
                <div
                  className={`toggle-switch toggle-switch-drawer ${
                    form.isDefault ? "is-on" : ""
                  }`}
                  onClick={() => toggleBool("isDefault")}
                >
                  <div className="toggle-switch-knob" />
                </div>
              </div>

              <div
                className="drawer-actions drawer-actions-left"
                style={{ display: "flex", gap: 10 }}
              >
                <button className="btn-close" type="button" onClick={onClose}>
                  Close
                </button>

                <button className="btn-reset" type="button" onClick={onReset}>
                  Reset
                </button>

                <button className="btn-save" type="button" onClick={onSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};


//ProductStatusDrawer//
type ProductDrawerBoolKey =
  | "song"
  | "track"
  | "release"
  | "cue"
  | "showhide"
  | "owned"
  | "interco"
  | "isDefault";

export type ProductStatusDrawerMode = "add" | "edit";

export interface ProductStatusDrawerForm {
  id: string;
  shortCode: string;
  name: string;
  song: boolean;
  track: boolean;
  release: boolean;
  cue: boolean;
  showhide: boolean;
  owned: boolean;
  interco: boolean;
  isDefault: boolean;
  color: string;
}

type ProductDrawerToggleField = Exclude<ProductDrawerBoolKey, "isDefault">;

const productDrawerToggleConfig: { key: ProductDrawerToggleField; label: string }[] =
  [
    { key: "song", label: "Song YN" },
    { key: "track", label: "Track YN" },
    { key: "release", label: "Release YN" },
    { key: "cue", label: "Cue YN" },
    { key: "showhide", label: "Show Hide YN" },
    { key: "owned", label: "Owned YN" },
    { key: "interco", label: "InterCo YN" },
  ];

type ProductProps = {
  open: boolean;
  mode: ProductStatusDrawerMode;
  form: ProductStatusDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof ProductStatusDrawerForm>(
    field: K,
    value: ProductStatusDrawerForm[K]
  ) => void;

  toggleBool: (field: ProductDrawerBoolKey) => void;
};

export const ProductStatusDrawer: React.FC<ProductProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  toggleBool,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer – AccountStatus-style */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Product Status" : "Edit Product Status"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* Use ID */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="psUseId">
                  Use ID
                </label>
                <input
                  id="psUseId"
                  className="drawer-input"
                  type="number"
                  placeholder="Use ID e.g. 30"
                  value={form.id}
                  onChange={(e) => updateField("id", e.target.value)}
                />
              </div>
            </div>

            {/* Name + Short Code row */}
            <div
              className="drawer-form-row"
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-end",
                marginBottom: 16,
              }}
            >
              <div className="drawer-field" style={{ flex: 1 }}>
                <label className="drawer-label" htmlFor="psName">
                  Product Status Name
                </label>
                <input
                  id="psName"
                  className="drawer-input"
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="drawer-field" style={{ flex: 1 }}>
                <label className="drawer-label" htmlFor="psShortCode">
                  Product Status Short Code
                </label>
                <input
                  id="psShortCode"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.shortCode}
                  onChange={(e) => updateField("shortCode", e.target.value)}
                />
              </div>
            </div>

            {/* Slider toggles */}
            <div className="drawer-toggle-row">
              {productDrawerToggleConfig.map(({ key, label }) => (
                <div className="drawer-toggle-item" key={key}>
                  <span className="drawer-toggle-label">{label}</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form[key] ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool(key)}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>
              ))}
            </div>

            {/* Default + Buttons under it */}
            <div className="drawer-bottom-row">
              <div className="drawer-bottom-group">
                <div className="drawer-toggle-item">
                  <span className="drawer-toggle-label">Default</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form.isDefault ? "is-on" : ""
                    }`}
                    onClick={() => toggleBool("isDefault")}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>

                <div className="drawer-actions drawer-actions-under">
                  <button className="btn-close" type="button" onClick={onClose}>
                    Close
                  </button>

                  <button className="btn-reset" type="button" onClick={onReset}>
                    Reset
                  </button>

                  <button className="btn-save" type="button" onClick={onSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
            {/* end bottom row */}
          </div>
        </div>
      </aside>
    </>
  );
};


//ProductStatusEXT//
export type ProductStatusExtDrawerMode = "add" | "edit";

export interface ProductStatusExtDrawerForm {
  useId: string;
  shortCode: string;
  name: string;
  isDefault: boolean;
}

type ProductStatusExtDrawerProps = {
  open: boolean;
  mode: ProductStatusExtDrawerMode;
  form: ProductStatusExtDrawerForm;

  onClose: () => void;
  onReset: () => void;
  onSave: () => void;

  updateField: <K extends keyof ProductStatusExtDrawerForm>(
    field: K,
    value: ProductStatusExtDrawerForm[K]
  ) => void;

  onToggleDefault: () => void;
};

export const ProductStatusExtDrawer: React.FC<ProductStatusExtDrawerProps> = ({
  open,
  mode,
  form,
  onClose,
  onReset,
  onSave,
  updateField,
  onToggleDefault,
}) => {
  return (
    <>
      {/* Backdrop */}
      <div
        id="drawerBackdrop"
        className={`drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside id="editDrawer" className={`drawer ${open ? "open" : ""}`}>
        <div className="drawer-inner">
          <div className="drawer-header">
            <div className="drawer-title" id="drawerTitle">
              {mode === "add" ? "Add Product Status Ext" : "Edit Product Status Ext"}
            </div>
            <button
              className="drawer-close"
              id="drawerCloseBtn"
              type="button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div className="drawer-card">
            {/* Row 1: Use ID */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="peUseId">
                  Product Status Ext ID
                </label>
                <input
                  id="peUseId"
                  className="drawer-input"
                  type="number"
                  placeholder="Use ID"
                  value={form.useId}
                  onChange={(e) => updateField("useId", e.target.value)}
                />
              </div>
            </div>

            {/* Row 2: Name + Short Code */}
            <div className="drawer-form-row">
              <div className="drawer-field">
                <label className="drawer-label" htmlFor="peName">
                  Name
                </label>
                <input
                  id="peName"
                  className="drawer-input"
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="drawer-field">
                <label className="drawer-label" htmlFor="peShortCode">
                  Short Code
                </label>
                <input
                  id="peShortCode"
                  className="drawer-input"
                  type="text"
                  placeholder="Short Code"
                  value={form.shortCode}
                  onChange={(e) => updateField("shortCode", e.target.value)}
                />
              </div>
            </div>

            {/* Default + buttons */}
            <div className="drawer-bottom-row">
              <div className="drawer-bottom-group">
                <div className="drawer-toggle-item">
                  <span className="drawer-toggle-label">Default</span>
                  <div
                    className={`toggle-switch toggle-switch-drawer ${
                      form.isDefault ? "is-on" : ""
                    }`}
                    onClick={onToggleDefault}
                  >
                    <div className="toggle-switch-knob" />
                  </div>
                </div>

                <div className="drawer-actions drawer-actions-under">
                  <button className="btn-close" type="button" onClick={onClose}>
                    Close
                  </button>

                  <button className="btn-reset" type="button" onClick={onReset}>
                    Reset
                  </button>

                  <button className="btn-save" type="button" onClick={onSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
            {/* end bottom row */}
          </div>
        </div>
      </aside>
    </>
  );
};
